export interface AddressData {
    id: string;
    text: string;
    description?: string;
    type?: string;
    isContainer?: boolean;
}

export interface FullAddress {
    line1: string;
    line2: string;
    line3: string;
    city: string;
    provinceName: string;
    postalCode: string;
    countryName: string;
}

export class AddressService {
    private locationTimer: { [key: string]: number } = {};
    private lastQuery: { [key: string]: string } = {};

    public initialize(): void {
        this.bindEventHandlers();
        this.bindFormSubmissionHandler();
    }

    // Helper methods for modern DOM manipulation
    private show(element: HTMLElement): void {
        element.style.display = '';
    }

    private hide(element: HTMLElement): void {
        element.style.display = 'none';
    }

    private isVisible(element: HTMLElement): boolean {
        return element.offsetParent !== null;
    }

    private setFieldValue(element: HTMLInputElement | HTMLSelectElement, value: string): void {
        element.value = value;
        element.dispatchEvent(new Event('change', { bubbles: true }));
    }

    private copyFieldValue(sourceField: HTMLInputElement | HTMLSelectElement, targetField: HTMLInputElement | HTMLSelectElement): void {
        if (sourceField && targetField) {
            this.setFieldValue(targetField, sourceField.value);
        }
    }

    private bindEventHandlers(): void {
        document.addEventListener("click", (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const trigger = target.closest(".js-get-address-input-trigger");
            if (trigger instanceof HTMLElement) {
                this.handleGetAddressInput(trigger);
            }
        });

        document.addEventListener("input", (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const input = target.closest(".address-input-container .addresslookup-textsearch");
            if (input instanceof HTMLInputElement) {
                this.handleAddressLookupInput(input);
            }
        });

        document.addEventListener("input", (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const checkbox = target.closest(".js-same-address");
            if (checkbox instanceof HTMLInputElement) {
                this.handleSameAddressCheckbox(checkbox);
            }
        });

        // Keyboard navigation for address lookup results
        document.addEventListener("keydown", (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            if (target.matches(".addresslookup-textsearch")) {
                this.handleSearchInputKeydown(event as KeyboardEvent);
                return;
            }

            if (target.matches(".addresslookup-results-list li")) {
                this.handleResultItemKeydown(event as KeyboardEvent);
            }
        });
    }

    private handleSearchInputKeydown(event: KeyboardEvent): void {
        const input = event.target as HTMLInputElement;
        const container = input.closest(".addresslookup-container") as HTMLElement;
        if (!container) return;

        const resultsUl = container.querySelector(".addresslookup-results-list ul") as HTMLUListElement;
        if (!resultsUl) return;

        const items = resultsUl.querySelectorAll("li") as NodeListOf<HTMLLIElement>;
        if (items.length === 0) return;

        if (event.key === "ArrowDown") {
            event.preventDefault();
            items[0].focus();
        } else if (event.key === "Escape") {
            const resultsDiv = container.querySelector(".addresslookup-collapse-results") as HTMLElement;
            if (resultsDiv) {
                resultsDiv.classList.remove("in", "show");
                resultsDiv.setAttribute("aria-expanded", "false");
            }
        }
    }

    private handleResultItemKeydown(event: KeyboardEvent): void {
        const target = event.target as HTMLLIElement;
        const ul = target.closest("ul") as HTMLUListElement;
        if (!ul) return;

        const items = Array.from(ul.querySelectorAll("li")) as HTMLLIElement[];
        const currentIndex = items.indexOf(target);

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                if (currentIndex < items.length - 1) {
                    items[currentIndex + 1].focus();
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                if (currentIndex > 0) {
                    items[currentIndex - 1].focus();
                } else {
                    const container = target.closest(".addresslookup-container") as HTMLElement;
                    const input = container?.querySelector(".addresslookup-textsearch") as HTMLInputElement;
                    if (input) input.focus();
                }
                break;
            case "Enter":
            case " ":
                event.preventDefault();
                target.click();
                break;
            case "Escape": {
                event.preventDefault();
                const container = target.closest(".addresslookup-container") as HTMLElement;
                const resultsDiv = container?.querySelector(".addresslookup-collapse-results") as HTMLElement;
                if (resultsDiv) {
                    resultsDiv.classList.remove("in", "show");
                    resultsDiv.setAttribute("aria-expanded", "false");
                }
                const input = container?.querySelector(".addresslookup-textsearch") as HTMLInputElement;
                if (input) input.focus();
                break;
            }
            case "Tab": {
                const containerEl = target.closest(".addresslookup-container") as HTMLElement;
                const resultsDivEl = containerEl?.querySelector(".addresslookup-collapse-results") as HTMLElement;
                if (resultsDivEl && !event.shiftKey && currentIndex === items.length - 1) {
                    resultsDivEl.classList.remove("in", "show");
                    resultsDivEl.setAttribute("aria-expanded", "false");
                }
                break;
            }
        }
    }

    private handleGetAddressInput(target: HTMLElement): void {

        const addressInputFormSection = target.closest(".address-input-form-section");
        if (!addressInputFormSection) return;

        const addressInputTriggers =
            addressInputFormSection.querySelectorAll<HTMLElement>(".js-get-address-input-trigger");

        const addressInputContainers =
            addressInputFormSection.querySelectorAll<HTMLElement>(".address-input-container");

        const addressInputType = target.dataset.addressInputType;

        // Hide all triggers
        addressInputTriggers.forEach(trigger => this.hide(trigger));

        // Show triggers that don't match the current type
        addressInputTriggers.forEach(trigger => {
            if (trigger.dataset.addressInputType !== addressInputType) {
                this.show(trigger);
            }
        });

        // Hide all containers
        addressInputContainers.forEach(container => this.hide(container));

        // Show containers that match the current type
        addressInputContainers.forEach(container => {
            if (container.dataset.addressInputType === addressInputType) {
                this.show(container);
            }
        });
    }


    private handleAddressLookupInput(target: HTMLInputElement): void {
        const container = target.closest(".addresslookup-container") as HTMLElement | null;
        if (!container) return;

        const id = container.id;

        if (this.locationTimer[id] !== undefined) {
            clearTimeout(this.locationTimer[id]);
        }

        this.locationTimer[id] = window.setTimeout(() => {
            this.performLocationQuery(target, id);
        }, 800);
    }

    private performLocationQuery(input: HTMLInputElement, containerId: string): void {
        const text = input.value?.trim().toLowerCase();

        if (!text) return;

        if (text === this.lastQuery[containerId]) return;
        this.lastQuery[containerId] = text;

        const parent = document.getElementById(containerId);
        if (!parent) return;

        const resultsDiv = parent.querySelector(".addresslookup-collapse-results") as HTMLElement;
        const resultsLoad = parent.querySelector(".addresslookup-loading") as HTMLElement;

        if (resultsLoad) this.show(resultsLoad);

        // Fully close results so we can open it again
        if (resultsDiv) {
            resultsDiv.classList.remove("in");
            resultsDiv.setAttribute("aria-expanded", "false");
        }

        input.dispatchEvent(new CustomEvent("beginsearch"));

        fetch(`/umbraco/surface/AddressInputSurface/GetLocation?s=${encodeURIComponent(text)}`)
            .then(response => response.json())
            .then((data: AddressData[]) => {
                input.dispatchEvent(new CustomEvent("finishsearch"));

                if (resultsLoad) this.hide(resultsLoad);

                const resultsContainer = resultsDiv?.querySelector(".addresslookup-results-container");
                const resultsUl = resultsContainer?.querySelector(".addresslookup-results-list ul") as HTMLUListElement;
                const resultsNoResults = resultsContainer?.querySelector("p.addresslookup-results-none") as HTMLElement;

                if (!data || data.length === 0) {
                    if (resultsNoResults) this.show(resultsNoResults);
                    if (resultsUl?.parentElement) this.hide(resultsUl.parentElement);
                    
                    // Show results div (equivalent to bootstrap collapse show)
                    if (resultsDiv) {
                        resultsDiv.classList.add("in", "show");
                        resultsDiv.setAttribute("aria-expanded", "true");
                    }
                    return;
                }

                let addressList = "";
                for (const d of data) {
                    addressList += `<li tabindex="0" role="option" data-id="${d.id}" data-is-container="${d.isContainer || false}">${d.text}${d.description ? `, ${d.description}` : ""}, ${d.id.split("|", 1)[0] || ""}</li>`;
                }

                if (resultsUl) {
                    resultsUl.setAttribute("role", "listbox");
                    resultsUl.innerHTML = addressList;
                    resultsUl.querySelectorAll("li").forEach(li => {
                        li.addEventListener("click", this.selectAddress.bind(this));
                    });
                }

                if (resultsNoResults) this.hide(resultsNoResults);
                if (resultsUl?.parentElement) this.show(resultsUl.parentElement);
                
                // Show results div (equivalent to bootstrap collapse show)
                if (resultsDiv) {
                    resultsDiv.classList.add("in", "show");
                    resultsDiv.setAttribute("aria-expanded", "true");
                }
            })
            .catch(error => {
                console.error("Address lookup failed:", error);
                input.dispatchEvent(new CustomEvent("finishsearch"));
                if (resultsLoad) this.hide(resultsLoad);
            });
    }

    private selectAddress(event: MouseEvent): void {
        const target = event.currentTarget as HTMLElement;
        const parent = target.closest(".addresslookup-container") as HTMLElement;
        if (!parent) return;

        const loqateId = target.dataset.id;
        if (!loqateId) return;

        const isContainer = target.dataset.isContainer === "true";

        // If this is a container (e.g., Street, Postcode), drill down to show addresses within it
        if (isContainer) {
            this.drillDownContainer(parent, loqateId);
            return;
        }

        const loqateIdField = parent.querySelector(".js-address-loqate-id-field") as HTMLInputElement;
        if (loqateIdField) {
            this.setFieldValue(loqateIdField, loqateId);
        }

        const selectDiv = parent.querySelector(".addresslookup-collapse-select") as HTMLElement;
        if (selectDiv) {
            const textElement = selectDiv.querySelector("p");
            if (textElement) {
                textElement.textContent = target.textContent || "";
            }
            
            // Show select div (equivalent to bootstrap collapse show)
            selectDiv.classList.add("in", "show");
            selectDiv.setAttribute("aria-expanded", "true");
        }

        // Remove active class from siblings and add to current
        target.parentElement?.querySelectorAll("li").forEach(li => li.classList.remove("active"));
        target.classList.add("active");
    }

    private drillDownContainer(parent: HTMLElement, containerId: string): void {
        const resultsDiv = parent.querySelector(".addresslookup-collapse-results") as HTMLElement;
        const resultsLoad = parent.querySelector(".addresslookup-loading") as HTMLElement;
        const selectDiv = parent.querySelector(".addresslookup-collapse-select") as HTMLElement;

        if (selectDiv) {
            selectDiv.classList.remove("in", "show");
            selectDiv.setAttribute("aria-expanded", "false");
        }

        if (resultsLoad) this.show(resultsLoad);

        fetch(`/umbraco/surface/AddressInputSurface/DrillDownContainer?containerId=${encodeURIComponent(containerId)}`)
            .then(response => response.json())
            .then((data: AddressData[]) => {
                if (resultsLoad) this.hide(resultsLoad);

                const resultsContainer = resultsDiv?.querySelector(".addresslookup-results-container");
                const resultsUl = resultsContainer?.querySelector(".addresslookup-results-list ul") as HTMLUListElement;
                const resultsNoResults = resultsContainer?.querySelector("p.addresslookup-results-none") as HTMLElement;

                if (!data || data.length === 0) {
                    if (resultsNoResults) this.show(resultsNoResults);
                    if (resultsUl?.parentElement) this.hide(resultsUl.parentElement);

                    if (resultsDiv) {
                        resultsDiv.classList.add("in", "show");
                        resultsDiv.setAttribute("aria-expanded", "true");
                    }
                    return;
                }

                let addressList = "";
                for (const d of data) {
                    addressList += `<li tabindex="0" role="option" data-id="${d.id}" data-is-container="${d.isContainer || false}">${d.text}${d.description ? `, ${d.description}` : ""}, ${d.id.split("|", 1)[0] || ""}</li>`;
                }

                if (resultsUl) {
                    resultsUl.setAttribute("role", "listbox");
                    resultsUl.innerHTML = addressList;
                    resultsUl.querySelectorAll("li").forEach(li => {
                        li.addEventListener("click", this.selectAddress.bind(this));
                    });

                    const firstItem = resultsUl.querySelector("li") as HTMLLIElement;
                    if (firstItem) {
                        firstItem.focus();
                    }
                }

                if (resultsNoResults) this.hide(resultsNoResults);
                if (resultsUl?.parentElement) this.show(resultsUl.parentElement);

                if (resultsDiv) {
                    resultsDiv.classList.add("in", "show");
                    resultsDiv.setAttribute("aria-expanded", "true");
                }
            })
            .catch(error => {
                console.error("Address drill-down failed:", error);
                if (resultsLoad) this.hide(resultsLoad);
            });
    }

    private async getAndSetFullAddress(addressLookupContainer: HTMLElement, loqateId: string): Promise<boolean> {
        try {
            const fullAddress = await this.getFullAddress(loqateId);
            
            if (!fullAddress) {
                const collapseHelp = addressLookupContainer.querySelector(".addresslookup-collapse-help") as HTMLElement;
                if (collapseHelp) {
                    const helpElement = collapseHelp.querySelector(".addresslookup-help");
                    if (helpElement) {
                        helpElement.innerHTML = "<p>Couldn't retrieve full address details. Please enter your address manually.</p>";
                    }
                    
                    // Show help collapse
                    collapseHelp.classList.add("in", "show");
                    collapseHelp.setAttribute("aria-expanded", "true");

                    // Assuming App is available globally - will be handled in main app
                    if (window.App && window.App.smoothScrollTo) {
                        window.App.smoothScrollTo(collapseHelp);
                    }
                }

                return false;
            }

            const collapseHelp = addressLookupContainer.querySelector(".addresslookup-collapse-help") as HTMLElement;
            if (collapseHelp) {
                // Hide help collapse
                collapseHelp.classList.remove("in", "show");
                collapseHelp.setAttribute("aria-expanded", "false");
            }

            this.setFullAddress(addressLookupContainer, fullAddress);

            return true;
        } catch (error) {
            console.error("Failed to get full address:", error);
            return false;
        }
    }

    private async getFullAddress(loqateId: string): Promise<FullAddress> {
        const response = await fetch(`/umbraco/surface/AddressInputSurface/GetFullAddress?loqateId=${loqateId}`, {
            method: "GET"
        });
        
        if (!response.ok) {
            throw new Error(`Failed to fetch address: ${response.status}`);
        }
        
        return response.json();
    }

    private setFullAddress(addressLookupContainer: HTMLElement, fullAddress: FullAddress): void {
        const addressInputFormSection = addressLookupContainer.closest(".address-input-form-section") as HTMLElement;
        if (!addressInputFormSection) return;

        const addressInputContainers = addressInputFormSection.querySelectorAll(".address-input-container");

        // Set values for all address fields
        addressInputContainers.forEach(container => {
            const line1Field = container.querySelector(".js-address-line-1-field") as HTMLInputElement;
            if (line1Field) this.setFieldValue(line1Field, fullAddress.line1 || "");

            const line2Field = container.querySelector(".js-address-line-2-field") as HTMLInputElement;
            if (line2Field) this.setFieldValue(line2Field, fullAddress.line2 || "");

            const line3Field = container.querySelector(".js-address-line-3-field") as HTMLInputElement;
            if (line3Field) this.setFieldValue(line3Field, fullAddress.line3 || "");

            const cityField = container.querySelector(".js-address-city-field") as HTMLInputElement;
            if (cityField) this.setFieldValue(cityField, fullAddress.city || "");

            const countyField = container.querySelector(".js-address-county-field") as HTMLInputElement;
            if (countyField) this.setFieldValue(countyField, fullAddress.provinceName || "");

            const postcodeField = container.querySelector(".js-address-postcode-field") as HTMLInputElement;
            if (postcodeField) this.setFieldValue(postcodeField, fullAddress.postalCode || "");

            const countryField = container.querySelector(".js-address-country-field") as HTMLSelectElement;
            if (countryField) this.setFieldValue(countryField, fullAddress.countryName || "");
        });
    }

    private handleSameAddressCheckbox(target: HTMLInputElement): void {
        const checked = target.checked;

        const container = target.closest(".text.mb-4") as HTMLElement | null;
        if (!container) return;

        const addressForm = container.querySelector("#address") as HTMLElement | null;
        const addressBillingForm = container.querySelector("#address-billing") as HTMLElement | null;

        if (checked) {
            if (addressForm) {
                this.show(addressForm);

                // Set readonly on inputs and selects
                addressForm.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input, select")
                    .forEach(field => field.setAttribute("readonly", "readonly"));

                // Disable choices.js elements (visual)
                addressForm.querySelectorAll<HTMLElement>(".choices")
                    .forEach(choice => choice.classList.add("is-disabled"));
            }

            if (addressBillingForm) {
                this.hide(addressBillingForm);
            }

            // Copy values from shipping to billing
            if (addressForm && addressBillingForm) {
                const address1 = addressForm.querySelector<HTMLInputElement>(".js-address-line-1-field");
                const billingAddress1 = addressBillingForm.querySelector<HTMLInputElement>(".js-address-line-1-field");
                if (address1 && billingAddress1) this.copyFieldValue(address1, billingAddress1);

                const address2 = addressForm.querySelector<HTMLInputElement>(".js-address-line-2-field");
                const billingAddress2 = addressBillingForm.querySelector<HTMLInputElement>(".js-address-line-2-field");
                if (address2 && billingAddress2) this.copyFieldValue(address2, billingAddress2);

                const addressCity = addressForm.querySelector<HTMLInputElement>(".js-address-city-field");
                const billingCity = addressBillingForm.querySelector<HTMLInputElement>(".js-address-city-field");
                if (addressCity && billingCity) this.copyFieldValue(addressCity, billingCity);

                const addressCounty = addressForm.querySelector<HTMLInputElement>(".js-address-county-field");
                const billingCounty = addressBillingForm.querySelector<HTMLInputElement>(".js-address-county-field");
                if (addressCounty && billingCounty) this.copyFieldValue(addressCounty, billingCounty);

                const addressPostcode = addressForm.querySelector<HTMLInputElement>(".js-address-postcode-field");
                const billingPostcode = addressBillingForm.querySelector<HTMLInputElement>(".js-address-postcode-field");
                if (addressPostcode && billingPostcode) this.copyFieldValue(addressPostcode, billingPostcode);

                const addressCountry = addressForm.querySelector<HTMLSelectElement>(".js-address-country-field");
                const billingCountry = addressBillingForm.querySelector<HTMLSelectElement>(".js-address-country-field");
                if (addressCountry && billingCountry) this.copyFieldValue(addressCountry, billingCountry);
            }
        } else {
            if (addressForm) {
                this.hide(addressForm);

                // Remove readonly from inputs and selects
                addressForm.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input, select")
                    .forEach(field => field.removeAttribute("readonly"));

                // Enable choices.js elements (visual)
                addressForm.querySelectorAll<HTMLElement>(".choices")
                    .forEach(choice => choice.classList.remove("is-disabled"));
            }

            if (addressBillingForm) {
                // Clear billing form values
                addressBillingForm.querySelectorAll<HTMLInputElement>("input")
                    .forEach(input => this.setFieldValue(input, ""));

                this.show(addressBillingForm);
            }
        }

        // Hide address input triggers
        if (addressForm) {
            addressForm.querySelectorAll<HTMLElement>(".js-get-address-input-trigger")
                .forEach(trigger => this.hide(trigger));
        }
    }


    private bindFormSubmissionHandler(): void {
        // The only way to hook into form submission before validation
        document.addEventListener("submit", async (e) => {
            const form = e.target as HTMLFormElement;
            
            // Get the address lookup container element, only perform additional checks if it's present
            const addressLookupContainer = form.querySelector(".addresslookup-container") as HTMLElement;
            if (!addressLookupContainer) {
                return;
            }

            e.preventDefault();

            // Check if form is valid (we'll assume validation happens elsewhere or use basic validity)
            if (form.checkValidity()) {
                const loqateIdField = addressLookupContainer.querySelector(".js-address-loqate-id-field") as HTMLInputElement;
                if (!loqateIdField || !this.isVisible(loqateIdField)) {
                    if (loqateIdField) {
                        this.setFieldValue(loqateIdField, 'found');
                    }

                    // The field for holding the Loqate Id is gone, we're likely looking at the manual address form, disregard and submit
                    form.submit();
                    return;
                }

                const loqateId = loqateIdField.value;

                try {
                    const success = await this.getAndSetFullAddress(addressLookupContainer, loqateId);
                    if (success) {
                        // All is good, submit the form
                        form.submit();
                    }
                } catch (error) {
                    console.error("Address validation failed:", error);
                }
            }
        });
    }
}

// Global window extension for compatibility
declare global {
    interface Window {
        App?: any;
    }
}
